import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { getLibrary } from "@/lib/api";
import StoneLogo from "@/components/StoneLogo";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Filter } from "lucide-react";

const LibraryPage = () => {
  const [stones, setStones] = useState([]);
  const [category, setCategory] = useState("Todos");
  const [active, setActive] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    getLibrary()
      .then((d) => setStones(d.stones || []))
      .catch(() => {});
  }, []);

  // Auto-open modal when ?stone=<slug> is present and stones are loaded
  useEffect(() => {
    const slug = searchParams.get("stone");
    if (!slug || stones.length === 0) return;
    const match = stones.find((s) => s.slug === slug);
    if (match) {
      setActive(match);
      if (match.category) setCategory(match.category);
    }
  }, [searchParams, stones]);

  const handleCloseModal = () => {
    setActive(null);
    if (searchParams.get("stone")) {
      const next = new URLSearchParams(searchParams);
      next.delete("stone");
      setSearchParams(next, { replace: true });
    }
  };

  const categories = ["Todos", ...Array.from(new Set(stones.map((s) => s.category)))];
  const filtered = category === "Todos" ? stones : stones.filter((s) => s.category === category);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="library-page">
      <header className="border-b border-[#1f1f1f] backdrop-blur-xl bg-[#0A0A0A]/70 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="library-back-link"><StoneLogo size={44} wordmarkSize={24} /></Link>
          <Link to="/chat" data-testid="library-launch-chat">
            <Button className="rounded-full bg-[#F5F5F7] text-black hover:bg-white px-5 h-9 text-[13px] font-medium">
              Abrir Chat
            </Button>
          </Link>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-10">
        <p className="text-[11px] tracking-[0.32em] uppercase text-zinc-500 mb-6">Biblioteca Mineral</p>
        <h1 className="silver-text text-5xl md:text-6xl font-light tracking-tighter max-w-3xl" style={{ fontFamily: "Outfit" }}>
          O catálogo vivo das pedras decorativas.
        </h1>
        <p className="text-zinc-400 mt-5 max-w-2xl">
          Uma curadoria contínua de mármores, quartzitos, ônix e travertinos com propriedades técnicas verificadas.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="flex items-center gap-2 flex-wrap mb-10" data-testid="library-filters">
          <Filter size={14} className="text-zinc-500 mr-1" />
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              data-testid={`library-filter-${c.toLowerCase()}`}
              className={`text-[12px] px-4 py-1.5 rounded-full border transition ${
                category === c
                  ? "bg-[#F5F5F7] text-black border-[#F5F5F7]"
                  : "border-[#262626] text-zinc-400 hover:text-white hover:border-[#3a3a3a]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((s, i) => (
            <motion.button
              key={s.slug}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              onClick={() => setActive(s)}
              data-testid={`stone-card-${s.slug}`}
              className="text-left rounded-2xl border border-[#1f1f1f] bg-[#0F0F10] overflow-hidden hover:border-[#3a3a3a] transition-all group"
            >
              <div className="aspect-[4/3] overflow-hidden bg-[#1a1a1a]">
                <img
                  src={s.image}
                  alt={s.name}
                  className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
                />
              </div>
              <div className="p-5">
                <div className="text-[10.5px] tracking-[0.22em] uppercase text-zinc-500 mb-1.5">{s.category}</div>
                <div className="text-[17px] text-white font-medium tracking-tight" style={{ fontFamily: "Outfit" }}>{s.name}</div>
                <div className="text-[12.5px] text-zinc-500 mt-0.5">{s.origin}</div>
                <p className="text-[13px] text-zinc-400 mt-3 leading-relaxed line-clamp-2">{s.summary}</p>
              </div>
            </motion.button>
          ))}
        </div>
      </section>

      {active && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center p-4"
          onClick={handleCloseModal}
          data-testid="stone-detail-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#0F0F10] border border-[#262626] rounded-3xl max-w-3xl w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="aspect-[16/8] bg-[#1a1a1a] overflow-hidden">
              <img src={active.image} alt={active.name} className="w-full h-full object-cover" />
            </div>
            <div className="p-7">
              <div className="text-[10.5px] tracking-[0.22em] uppercase text-zinc-500 mb-1.5">{active.category} · {active.origin}</div>
              <h3 className="text-3xl font-light tracking-tight" style={{ fontFamily: "Outfit" }}>{active.name}</h3>
              <p className="text-zinc-400 mt-3 leading-relaxed">{active.summary}</p>
              <div className="grid grid-cols-3 gap-3 mt-6">
                <div className="rounded-xl border border-[#1f1f1f] p-4">
                  <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">Mohs</div>
                  <div className="text-2xl text-white mt-1" style={{ fontFamily: "Outfit" }}>{active.mohs}</div>
                </div>
                <div className="rounded-xl border border-[#1f1f1f] p-4">
                  <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">Absorção</div>
                  <div className="text-2xl text-white mt-1" style={{ fontFamily: "Outfit" }}>{active.absorption}%</div>
                </div>
                <div className="rounded-xl border border-[#1f1f1f] p-4">
                  <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">Densidade</div>
                  <div className="text-2xl text-white mt-1" style={{ fontFamily: "Outfit" }}>{active.density}</div>
                </div>
              </div>
              <div className="mt-6">
                <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 mb-2">Aplicações</div>
                <div className="flex flex-wrap gap-2">
                  {active.applications.map((a) => (
                    <span key={a} className="text-[12px] px-3 py-1 rounded-full bg-[#161616] border border-[#262626] text-zinc-300">{a}</span>
                  ))}
                </div>
              </div>
              <div className="mt-7 flex gap-3 justify-end">
                <Button
                  onClick={handleCloseModal}
                  variant="ghost"
                  className="rounded-full border border-[#262626] text-zinc-300 hover:text-white hover:bg-white/5 h-10 px-5 text-[13px]"
                  data-testid="modal-close-btn"
                >
                  Fechar
                </Button>
                <Link to="/chat" data-testid="modal-ask-stoneia">
                  <Button className="rounded-full bg-[#F5F5F7] text-black hover:bg-white h-10 px-5 text-[13px] font-medium">
                    Perguntar à StoneIA
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default LibraryPage;
